/** 代表キーの判定に必要な最小の operation。呼び出し側が coordKey を作って渡す */
export type CanonicalCoordKeySource = {
  id: number;
  action: '積' | '降';
  departureAt: string;
  drivingDurationSeconds: number;
  coordKey: string;
};

/**
 * operation 列から「operationId → 代表の座標キー」を作る。
 *
 * arrivalAt → departureAt → id 昇順に並べた列を先頭から走査し、次の 3 条件が
 * すべて揃ったときだけ直前の代表キーを引き継ぐ。1 つでも欠ければ自分の coordKey が
 * 自分の代表になる。
 *
 *   (a) 直前の operation と同じ kind（積 / 降）
 *   (b) 直前の operation と departureAt が一致
 *   (c) 自分の移動時間が 0
 *
 * Why 座標を丸めないのか: 丸めは境界で必ず割れる（log_web_issues#314 の 6 桁丸めでも
 * 139.523381 と 139.523386 は別値）。配車アルゴリズムは同じ場所での連続作業を
 * 移動時間0 で表現しているので、そちらを地点の同一性の根拠にする。
 *
 * Why not (c) だけを条件にする: 固定ルート適用で作られた既存 operation は全件
 * driving_duration_seconds: 0 (apply_to_existing_planning_service.rb:651) なので、
 * A店 09:00 と B店 10:00 が連鎖して 1 カードになる。(b) を課すことで既存データにも
 * そのまま効き、バックフィルもリリース順の制約も要らなくなる。
 *
 * Why (a) kind を見るか: 積降が同時発生する業態では同じ地点に積と降が並ぶ。
 * カードは (kind, placeKey) で引くので kind を跨いで寄せてはいけない。
 *
 * Why not kind ごとに直前状態を持つ: 走査は「直前の 1 件」だけを見るので、
 * 降A → 積X → 降B のように kind が交互に並ぶと降A と降B は比較されない。
 * kind ごとに連鎖を続ける実装も可能だが、現状（座標キーが違えば別カード）から
 * 統合が増える方向の変更になるため採らない。挟まれたケースは従来どおり別カードで、
 * 悪化はしない。
 *
 * Why not 戻り値のキーを座標キーにする: (1) 同じ座標キーが積と降で別の代表へ寄るとき
 * 後書きが勝つ (2) 同じ地点への再訪（移動時間>0 で自分が代表）が先の寄せを上書きする、
 * の 2 つで壊れる。operation ごとに持てば衝突しない。
 */
export function buildCanonicalCoordKeys(
  ops: readonly CanonicalCoordKeySource[],
): Map<number, string> {
  const canonicalByOperationId = new Map<number, string>();
  let prev: CanonicalCoordKeySource | null = null;
  let prevCanonical: string | null = null;

  for (const op of ops) {
    const inherits = prev != null
      && prevCanonical != null
      && prev.action === op.action
      && prev.departureAt === op.departureAt
      && op.drivingDurationSeconds === 0;
    const canonical = inherits ? prevCanonical : op.coordKey;
    canonicalByOperationId.set(op.id, canonical);
    prev = op;
    prevCanonical = canonical;
  }

  return canonicalByOperationId;
}
